import moment from 'moment-timezone'
import { constants } from '@adc/ui-core'

const { UI_DATE_FORMAT } = constants.statics

export const PACIFIC_TIMEZONE = 'America/Los_Angeles'
export const API_DATE_AGNOSTIC_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
export const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]'
export const API_LOCAL_TIME_FORMAT = 'HH:mm:ss'
export const API_LOCAL_DATE_FORMAT = 'YYYY-MM-DD'
export const UI_DATE_ALTERNATE_FORMAT = 'MMM DD, YYYY'
export const UI_DATE_TIME_FORMAT = 'MMM DD, YYYY h:mm a'
export const UI_TIME_FORMAT = 'hh:mm A'
export const UI_TIME_FORMAT_WITH_SECONDS = 'hh:mm:ss A'

export const convertDateFormat = (value, oldFormat, newFormat) => {
  if (!value) return value
  return moment(value, oldFormat).format(newFormat)
}

/**
 * Expected input would be similar to 10:00:00 or 16:30:00
 */
export const convertISOTimeToString = value => {
  if (!value) return value
  const time = moment(value, API_LOCAL_TIME_FORMAT)
  return time.format(UI_TIME_FORMAT)
}

/**
 * Expected input would be similar to 10:00 AM or 04:30 PM
 */
export const convertStringTimeToISO = value => {
  if (!value) return value
  const time = moment(value, UI_TIME_FORMAT)
  return moment(time.toISOString()).format(API_LOCAL_TIME_FORMAT)
}

/**
 * Expected input would be similar to 10:00:00 or 16:30:45
 * Converts to 12-hour format with seconds: 10:00:00 AM or 04:30:45 PM
 * Falls back to parsing without seconds (10:00 -> 10:00:00 AM)
 */
export const convertISOTimeToStringWithSeconds = value => {
  if (!value) return value
  const time = moment(value, [API_LOCAL_TIME_FORMAT, 'HH:mm'], true)
  return time.isValid() ? time.format(UI_TIME_FORMAT_WITH_SECONDS) : value
}

/**
 * Expected input would be similar to 10:00:00 AM or 04:30:45 PM
 * Converts to 24-hour format with seconds: 10:00:00 or 16:30:45
 * Falls back to parsing without seconds (10:00 AM -> 10:00:00)
 */
export const convertStringTimeWithSecondsToISO = value => {
  if (!value) return value
  const time = moment(
    value,
    [UI_TIME_FORMAT_WITH_SECONDS, UI_TIME_FORMAT],
    true
  )
  return time.isValid() ? time.format(API_LOCAL_TIME_FORMAT) : value
}

export const convertToMilitaryTime = value => {
  if (!value) return value
  const time = moment(value, UI_TIME_FORMAT)
  return time.isValid() ? time.format('HH:mm') : value
}

export const convertToNonMilitaryTime = value => {
  if (!value) return value
  const time = moment(value, 'HH:mm')
  return time.isValid() ? time.format(UI_TIME_FORMAT) : value
}

/**
 * Important... unless you pass "asLocal", plain dates will be treated as PST and
 * formated as utc so for example, "5/9/2018" becomes "2018-05-09T00:00:00Z"
 *
 * If you pass asLocal as true, then the return will be a datetime relative to the local time.
 * So for example, "5/9/2018" becomes "2018-05-09T07:00:00Z" in PDT
 */
export const formatDateUiToApi = (
  value,
  asLocal = false,
  apiFormat = API_DATE_FORMAT
) => {
  if (!value) return null // send null if empty string or other falsey
  const date = asLocal
    ? moment(value, UI_DATE_FORMAT)
    : moment.tz(value, UI_DATE_FORMAT, PACIFIC_TIMEZONE)
  return date.isValid() ? date.utc().format(apiFormat) : value
}

/**
 * Dates will be treated as UTC
 * Display format allows clients to use their custom format, default MM/DD/YYYY
 * In case that the format has time, the time will be in PST as well
 * applyTimezone param to conditionally allow not applying pacific timezone offset on case by case basis.
 */
export const formatDateApiToUi = (
  value,
  displayFormat = UI_DATE_FORMAT,
  applyTimezone = true
) => {
  if (!value) return value
  const date = applyTimezone
    ? moment.utc(value, API_DATE_FORMAT).tz(PACIFIC_TIMEZONE)
    : moment.utc(value, API_DATE_FORMAT)
  return date.isValid() ? date.format(displayFormat) : value
}

/**
 * True DateTime fields are truly UTC and must be converted to PST
 */
export const formatDateTimeApiToUi = (value, midnightAllowed = true) => {
  if (!value) return value
  const date = moment.utc(value, API_DATE_FORMAT).tz(PACIFIC_TIMEZONE)
  let formattedDate
  if (date.isValid()) {
    const uiDateTime = date.format(UI_DATE_TIME_FORMAT)
    formattedDate = midnightAllowed
      ? uiDateTime
      : uiDateTime.replace(' 12:00 am', '')
  } else {
    formattedDate = value
  }
  return formattedDate
}

/**
 * Build DateTime based on date and time. Date is assumed to be PST
 */
export const formatDateTimeUiToApi = (day, time) => {
  if (!day || !time) return null // send null if empty string or other falsey
  const date = moment
    .tz(
      `${day} ${time}`,
      `${UI_DATE_FORMAT} ${UI_TIME_FORMAT}`,
      PACIFIC_TIMEZONE
    )
    .utc()
  return date.isValid() ? date.format(API_DATE_FORMAT) : null
}

/**
 * Build Date specifying parsing pattern and shifting timezone
 * returns to this format 'MMM DD, YYYY'
 */
export const formatDateApiToUiFromPattern = (value, pattern) => {
  if (!value) return value
  const date = moment.utc(value, pattern).tz(PACIFIC_TIMEZONE)
  return date.isValid() ? date.format(UI_DATE_ALTERNATE_FORMAT) : value
}

/**
 * Build Date specifying parsing pattern to this format 'MMM DD, YYYY'
 */
export const formatDateFromPattern = (
  value,
  pattern,
  format = UI_DATE_ALTERNATE_FORMAT
) => {
  if (!value) return value
  const date = moment.utc(value, pattern)
  return date.isValid() ? date.utc().format(format) : value
}
